import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { LEAD_STATUSES, LEAD_STATUS_LABELS } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { PreviewLeadPicker } from '@/features/emailTemplates/PreviewLeadPicker';
import leadStyles from '@/features/leads/Leads.module.css';
import styles from '@/pages/ProjectsPage.module.css';

export type SimulateStep = {
  step_order: number;
  channel?: string;
  subject: string;
  body_text: string;
  warnings?: string[];
  skipped?: boolean;
  email_template_id?: string | null;
};

type Props = {
  projectId: string;
  /** Wizard: preview vóór campagne bestaat */
  sequenceId?: string;
  /** Command center: bestaande campagne */
  campaignId?: string;
  variantId?: string;
  onVariantIdChange?: (id: string) => void;
  experimentVariants?: { id: string; variant_key: string }[];
  showSeedSend?: boolean;
};

export function CampaignSimulatePanel({
  projectId,
  sequenceId,
  campaignId,
  variantId,
  onVariantIdChange,
  experimentVariants,
  showSeedSend,
}: Props) {
  const seedDefaultsQ = useQuery({
    queryKey: ['seed-email-defaults', projectId],
    enabled: Boolean(projectId) && showSeedSend && Boolean(campaignId),
    queryFn: () =>
      apiFetch<{ suggested_test_emails: string[] }>(
        `/api/campaigns/seed-email-defaults?project_id=${projectId}`,
      ),
  });

  const [leadId, setLeadId] = useState('');
  const [assumedTags, setAssumedTags] = useState('');
  const [assumedStatus, setAssumedStatus] = useState('contacted');
  const [seedEmails, setSeedEmails] = useState('');

  useEffect(() => {
    const suggested = seedDefaultsQ.data?.suggested_test_emails ?? [];
    if (suggested.length && !seedEmails) setSeedEmails(suggested.join(', '));
  }, [seedDefaultsQ.data, seedEmails]);

  const simulate = useMutation({
    mutationFn: () => {
      if (!leadId) throw new Error('Kies een lead');
      if (campaignId) {
        return apiFetch<{
          simulation: SimulateStep[];
          branch_scenario?: { path_step_orders: number[] };
          variant: { variant_key: string } | null;
        }>(`/api/campaigns/${campaignId}/simulate`, {
          method: 'POST',
          body: JSON.stringify({
            lead_id: leadId,
            assumed_tags: assumedTags
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean),
            assumed_status: assumedStatus || undefined,
            ...(variantId ? { variant_id: variantId } : {}),
          }),
        });
      }
      if (!sequenceId) throw new Error('Geen sequence');
      return apiFetch<{ simulation: SimulateStep[]; branch_scenario?: { path_step_orders: number[] } }>(
        '/api/campaigns/simulate-preview',
        {
          method: 'POST',
          body: JSON.stringify({
            project_id: projectId,
            sequence_id: sequenceId,
            lead_id: leadId,
            assumed_tags: assumedTags
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean),
            assumed_status: assumedStatus || undefined,
          }),
        },
      );
    },
  });

  const seedSend = useMutation({
    mutationFn: () => {
      if (!campaignId || !leadId) throw new Error('Campagne en lead verplicht');
      const test_emails = seedEmails
        .split(/[,;\s]+/)
        .map((e) => e.trim())
        .filter(Boolean);
      if (!test_emails.length) throw new Error('Vul test-e-mailadressen in');
      return apiFetch<{ queued: number }>(`/api/campaigns/${campaignId}/seed-send`, {
        method: 'POST',
        body: JSON.stringify({
          lead_id: leadId,
          test_emails,
          step_order: 0,
          ...(variantId ? { variant_id: variantId } : {}),
        }),
      });
    },
  });

  return (
    <div className={styles.formGrid}>
      <PreviewLeadPicker projectId={projectId} value={leadId} onChange={setLeadId} />

      <Field label="Aangenomen tags (branch-test)">
        <TextInput value={assumedTags} onChange={(e) => setAssumedTags(e.target.value)} placeholder="vip" />
      </Field>
      <Field label="Aangenomen status">
        <select
          className={leadStyles.filterInput}
          value={assumedStatus}
          onChange={(e) => setAssumedStatus(e.target.value)}
        >
          {LEAD_STATUSES.map((s) => (
            <option key={s} value={s}>
              {LEAD_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </Field>

      {experimentVariants?.length ? (
        <Field label="Experiment-variant (simulate)">
          <select
            className={leadStyles.filterInput}
            value={variantId ?? ''}
            onChange={(e) => onVariantIdChange?.(e.target.value)}
          >
            <option value="">Standaard sequence</option>
            {experimentVariants.map((v) => (
              <option key={v.id} value={v.id}>
                Variant {v.variant_key}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      <div className={styles.row}>
        <Button type="button" variant="ghost" disabled={simulate.isPending} onClick={() => simulate.mutate()}>
          {simulate.isPending ? 'Simuleren…' : 'Simuleer sequence'}
        </Button>
      </div>

      {simulate.isError && simulate.error instanceof Error ? (
        <p className={styles.mutedSmall}>{simulate.error.message}</p>
      ) : null}

      {simulate.data?.branch_scenario?.path_step_orders?.length ? (
        <p className={styles.mutedSmall}>
          Branch-pad: stap {simulate.data.branch_scenario.path_step_orders.join(' → ')}
        </p>
      ) : null}

      {simulate.data?.simulation?.length ? (
        <div className={styles.mutedSmall}>
          {'variant' in simulate.data && simulate.data.variant ? (
            <p>Variant: {simulate.data.variant.variant_key}</p>
          ) : null}
          {simulate.data.simulation.map((s) => (
            <details key={s.step_order} style={{ marginBottom: '0.75rem' }}>
              <summary>
                Stap {s.step_order}
                {s.channel ? ` (${s.channel})` : ''}: {s.skipped ? 'overgeslagen' : s.subject}
                {(s.warnings?.length ?? 0) > 0 ? ' ⚠' : ''}
              </summary>
              {s.warnings?.map((w) => (
                <p key={w} style={{ color: 'var(--color-warning, #b45309)' }}>
                  {w}
                </p>
              ))}
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>{s.body_text}</pre>
            </details>
          ))}
        </div>
      ) : null}

      {showSeedSend && campaignId ? (
        <>
          <Field label="Seed test — inbox(es), komma-gescheiden">
            <TextInput value={seedEmails} onChange={(e) => setSeedEmails(e.target.value)} />
          </Field>
          <Button
            type="button"
            variant="ghost"
            disabled={seedSend.isPending}
            onClick={() => seedSend.mutate()}
          >
            {seedSend.isPending ? 'Versturen…' : 'Seed send (stap 0)'}
          </Button>
          {seedSend.isSuccess ? (
            <p className={styles.mutedSmall}>{seedSend.data.queued} testmail(s) in queue.</p>
          ) : null}
          {seedSend.isError && seedSend.error instanceof Error ? (
            <p className={styles.mutedSmall}>{seedSend.error.message}</p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
